import React, { useState, useEffect } from 'react';
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { View, Text, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { supabase } from './lib/supabase';
import './index.css';
import Navbar from './components/Navbar';
import ImpersonationBanner from './components/ImpersonationBanner';
import MobileContainer from './components/MobileContainer';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import WebOnlyRoute from './components/WebOnlyRoute';
import { getImpersonationState, clearImpersonationState } from './lib/impersonation';
import Dashboard from './pages/Dashboard';
import AlternateDashboard from './pages/AlternateDashboard';
import ServiceRequests from './pages/ServiceRequests';
import MaintenancePlan from './pages/MaintenancePlan';
import Expenses from './pages/Expenses';
import Services from './pages/Services';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import Plans from './pages/Plans';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import ClaimAccount from './pages/ClaimAccount';
import InviteSignup from './pages/InviteSignup';
import Admin from './pages/admin';
import ProfileDetail from './pages/admin/ProfileDetail';
import AdminHomeDetail from './pages/admin/HomeDetail';
import VisitDetail from './pages/admin/VisitDetail';
import ServiceRequestDetail from './pages/admin/ServiceRequestDetail';
import ServiceProviderDetail from './pages/admin/ServiceProviderDetail';
import Inventory from './pages/Inventory';
import Assistant from './pages/Assistant';
import Conversations from './pages/Conversations';
import ConversationDetail from './pages/ConversationDetail';
import HoneyDoList from './pages/HoneyDoList';
import ManagedServices from './pages/ManagedServices';
import HomeDetail from './pages/HomeDetail';
import VisitPlanning from './pages/VisitPlanning';
import VisitConfirmation from './pages/VisitConfirmation';
import Visits from './pages/Visits';
import ContractorVisitDetail from './pages/ContractorVisitDetail';
import ContractorBidPage from './pages/ContractorBidPage';
import ScheduleWalkthrough from './pages/ScheduleWalkthrough';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Referrals from './pages/Referrals';
import AgentLanding from './pages/AgentLanding';
import Team from './pages/Team';
import { ActiveHomeProvider } from './contexts/ActiveHomeContext';
import { usePushNotifications } from './hooks/usePushNotifications';

/**
 * Custom hook that returns stable safe area insets on web
 * On web: Returns static zeros (no provider needed)
 * On native: Returns actual safe area insets from the device
 */
const usePlatformSafeAreaInsets = () => {
  // On web, return static zeros without calling the hook
  // This avoids needing SafeAreaProvider and prevents viewport change listeners
  if (Platform.OS === 'web') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  
  // On native (iOS/Android), get actual safe area insets from provider
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const insets = useSafeAreaInsets();
  return insets;
};

/**
 * Protected route component - moved outside App to prevent recreation on each render
 * This prevents React Router from remounting routes when App re-renders
 */
const ProtectedRoute = ({ children, session }: { children: React.ReactNode; session: any }) => {
  if (!session) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

/**
 * Main layout wrapper - moved outside App to prevent recreation on each render
 * This prevents React Router from remounting routes when App re-renders
 */
const MainLayout = ({ 
  children, 
  isImpersonating, 
  isSystemManager,
  insets,
  noPadding = false,
  backgroundColor = '#f9fafb',
  noTopPadding = false,
  useMobileContainer = false
}: { 
  children: React.ReactNode;
  isImpersonating: boolean;
  isSystemManager: boolean;
  insets: { top: number; right: number; bottom: number; left: number };
  noPadding?: boolean;
  backgroundColor?: string;
  noTopPadding?: boolean;
  useMobileContainer?: boolean;
}) => {
  if (useMobileContainer) {
    // For mobile container, wrap everything including navbar
    return (
      <MobileContainer>
        <View style={[styles.mainContainer, { paddingTop: Platform.OS !== 'web' ? insets.top : 0, backgroundColor }]}>
          {isImpersonating && <ImpersonationBanner />}
          <View style={[styles.mainContent, noTopPadding && styles.mainContentNoTopPadding]}>
            <View style={[styles.contentWrapper, !noPadding && styles.contentWrapperWithPadding]}>
              {children}
            </View>
          </View>
          <Navbar isSystemManager={isSystemManager} />
        </View>
      </MobileContainer>
    );
  }

  // For full-width layouts (admin, etc.)
  return (
    <View style={[styles.mainContainer, { paddingTop: Platform.OS !== 'web' ? insets.top : 0, backgroundColor }]}>
      {isImpersonating && <ImpersonationBanner />}
      <View style={[styles.mainContent, noTopPadding && styles.mainContentNoTopPadding]}>
        <View style={[styles.contentWrapper, !noPadding && styles.contentWrapperWithPadding]}>
          {children}
        </View>
      </View>
      <Navbar isSystemManager={isSystemManager} />
    </View>
  );
};

/**
 * Handles auth events that require navigation (e.g. PASSWORD_RECOVERY).
 * Must be rendered inside the Router so useNavigate is available.
 */
const AuthEventHandler = ({ passwordRecovery }: { passwordRecovery: boolean }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (passwordRecovery) {
      navigate('/reset-password', { replace: true });
    }
  }, [passwordRecovery, navigate]);

  return null;
};

const PushNotificationHandler = ({ userId }: { userId?: string }) => {
  const navigate = useNavigate();

  usePushNotifications(userId, (conversationId) => {
    navigate(`/conversations/${conversationId}`);
  });

  return null;
};

function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSystemManager, setIsSystemManager] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const insets = usePlatformSafeAreaInsets();

  useEffect(() => {
    // Check for impersonation state on mount
    const impersonationState = getImpersonationState();
    setIsImpersonating(!!impersonationState);

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      if (initialSession?.user) {
        checkSystemManagerStatus(initialSession.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Don't treat recovery as a normal login — redirect to the set-new-password page
        setSession(session);
        setPasswordRecovery(true);
        setLoading(false);
        return;
      }
      setPasswordRecovery(false);
      setSession(session);
      if (session?.user) {
        checkSystemManagerStatus(session.user.id);
      } else {
        setIsSystemManager(false);
        // Clear impersonation state on logout
        clearImpersonationState();
        setIsImpersonating(false);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkSystemManagerStatus = async (userId: string) => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_system_manager')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;
      
      setIsSystemManager(profile?.is_system_manager || false);
      
    } catch (error: any) {
      console.error("Profile fetch error:", error);
      setError(error.message);
      setIsSystemManager(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color="#000000" />
          {error && (
            <Text style={styles.errorText}>{error}</Text>
          )}
        </View>
      </View>
    );
  }

  // Use MemoryRouter for React Native, BrowserRouter for web
  const Router = Platform.OS === 'web' ? BrowserRouter : MemoryRouter;

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ActiveHomeProvider>
        <AuthEventHandler passwordRecovery={passwordRecovery} />
        <PushNotificationHandler userId={session?.user?.id} />
        <UpdateAvailableBanner />
        <Routes>
        {/* Public routes */}
        <Route
          path="/"
          element={session && !passwordRecovery ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/login"
          element={session && !passwordRecovery ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/reset-password"
          element={session ? <ResetPassword /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/invite-signup"
          element={<InviteSignup session={session} />}
        />
        <Route
          path="/claim"
          element={<ClaimAccount />}
        />
        <Route
          path="/onboarding"
          element={session ? <Navigate to="/plans" replace /> : <Onboarding />}
        />
        <Route
          path="/signup"
          element={session ? <Navigate to="/plans" replace /> : <Onboarding />}
        />
        <Route
          path="/agent/:slug"
          element={<AgentLanding />}
        />
        <Route
          path="/privacy-policy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="/terms-of-service"
          element={<TermsOfService />}
        />
        <Route
          path="/contractor/visit"
          element={<ContractorVisitDetail />}
        />
        <Route
          path="/contractor/bid"
          element={<ContractorBidPage />}
        />
        <Route
          path="/plans"
          element={
            <ProtectedRoute session={session}>
              <Plans />
            </ProtectedRoute>
          }
        />
        
        {/*
          Admin routes.
          - `/admin` (the shell with tabs) is native-friendly: only the My Day and Messages
            tabs render on iOS/Android (see NATIVE_ADMIN_TABS in src/pages/admin/index.tsx).
          - The detail routes below render raw DOM/Tailwind and are still web-only.
        */}
        <Route path="/admin" element={<ProtectedRoute session={session}><Admin /></ProtectedRoute>} />
        <Route path="/admin/profile" element={<ProtectedRoute session={session}><WebOnlyRoute message="This admin page is only available on the web."><ProfileDetail /></WebOnlyRoute></ProtectedRoute>} />
        <Route path="/admin/home" element={<ProtectedRoute session={session}><WebOnlyRoute message="This admin page is only available on the web."><AdminHomeDetail /></WebOnlyRoute></ProtectedRoute>} />
        <Route path="/admin/visit" element={<ProtectedRoute session={session}><WebOnlyRoute message="This admin page is only available on the web."><VisitDetail /></WebOnlyRoute></ProtectedRoute>} />
        <Route path="/admin/service-request/:id" element={<ProtectedRoute session={session}><WebOnlyRoute message="This admin page is only available on the web."><ServiceRequestDetail /></WebOnlyRoute></ProtectedRoute>} />
        <Route path="/admin/service-provider/:id" element={<ProtectedRoute session={session}><WebOnlyRoute message="This admin page is only available on the web."><ServiceProviderDetail /></WebOnlyRoute></ProtectedRoute>} />
        
        {/* Assistant route with special layout - kept for backward compatibility */}
        <Route
          path="/assistant"
          element={
            <ProtectedRoute session={session}>
              <View style={[styles.assistantContainer, { paddingTop: Platform.OS !== 'web' ? insets.top : 0 }]}>
                {isImpersonating && <ImpersonationBanner />}
                <View style={styles.assistantContent}>
                  <Assistant />
                </View>
                <Navbar isSystemManager={isSystemManager} />
              </View>
            </ProtectedRoute>
          }
        />

        {/* Conversations routes */}
        <Route
          path="/conversations"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Conversations />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/conversations/:conversationId"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true} backgroundColor="#ffffff">
                <ConversationDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Main app routes with standard layout - FLATTENED (no nested Routes) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} noPadding={true} backgroundColor="#000000" noTopPadding={true} useMobileContainer={true}>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard-alt"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <AlternateDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-home"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <HomeDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Inventory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-requests"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <ServiceRequests />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance-plan"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets}  backgroundColor="#ffffff" useMobileContainer={true}>
                <MaintenancePlan />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visit-plan"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} backgroundColor="#ffffff" noPadding={true} useMobileContainer={true}>
                <VisitPlanning />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visit-confirmation"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} backgroundColor="#ffffff" noPadding={true} useMobileContainer={true}>
                <VisitConfirmation />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visits"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Visits />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule-walkthrough"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <ScheduleWalkthrough />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Expenses />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/services"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Services />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/managed-services"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <ManagedServices />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/honeydolist"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <HoneyDoList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Profile session={session} />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true}>
                <Team />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute session={session}>
              <MainLayout isImpersonating={isImpersonating} isSystemManager={isSystemManager} insets={insets} useMobileContainer={true} noPadding={true}>
                <Referrals />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ActiveHomeProvider>
    </Router>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContent: {
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
  },
  adminContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    backgroundColor: '#f9fafb',
  },
  adminContent: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 32,
    ...(Platform.OS === 'web' && {
      maxWidth: 1280,
      marginHorizontal: 'auto',
      width: '100%',
    }),
  },
  assistantContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
    }),
  },
  assistantContent: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      paddingTop: 64,
    }),
    ...(Platform.OS !== 'web' && {
      paddingBottom: 64,
    }),
  },
  mainContainer: {
    flex: 1,
    minHeight: Platform.OS === 'web' ? 'var(--app-height)' as any : '100%',
    backgroundColor: '#f9fafb',
    ...(Platform.OS === 'web' && {
      flexDirection: 'row',
    }),
  },
  mainContent: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      paddingTop: 64,
    }),
    // On native the Navbar is rendered as a flex sibling at the bottom of
    // mainContainer (see Navbar.tsx), so it does not overlap content and we
    // do not need a bottom padding here. Adding one exposes the parent
    // mainContainer's background between the content and the navbar — which
    // shows up as e.g. a black bar on the homeowner dashboard.
  },
  mainContentNoTopPadding: {
    ...(Platform.OS === 'web' && {
      paddingTop: 0,
    }),
  },
  contentWrapper: {
    flex: 1,
    paddingTop: 32,
    ...(Platform.OS === 'web' && {
      paddingBottom: 32,
      maxWidth: 1280,
      marginHorizontal: 'auto',
      width: '100%',
    }),
  },
  contentWrapperWithPadding: {
    paddingHorizontal: 16,
  },
});

export default App;